import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Segmented, Select, TreeSelect, type FormInstance } from "antd";
import { useTranslation } from "react-i18next";

import {
  type CalendarEventFilterCondition,
  type CRMTagFilterCondition,
  type FilterOperator,
  type TraineeTagFilterCondition,
} from "@fitness-app/data-models/entities/AutomationTemplate";
import { CalendarEventType } from "@fitness-app/data-models/entities/CalendarEvent";
import { EmailClient } from "@fitness-app/data-models/entities/ProductsSettings";

import { LIST_ITEM_SEPARATOR } from "~/constants/separators";
import { useEmailLists } from "~/hooks/useEmailLists";
import { useTagOptions } from "~/hooks/useTagOptions";
import { type CRMTagFilterConditionField } from "~/modules/Automation/AutomatedMessagesForm/types";

const typeToHeight = {
  tags: 280,
  crmTags: 350,
  calendarEvents: 350,
  none: 100,
};

export const transformFromFiltersToFormData = (
  filters?: (TraineeTagFilterCondition | CalendarEventFilterCondition | CRMTagFilterCondition)[],
): (TraineeTagFilterCondition | CalendarEventFilterCondition | CRMTagFilterConditionField)[] | undefined => {
  return filters?.map((filter) =>
    filter.type === "crmTags"
      ? {
          ...filter,
          emailClients: filter.emailClients.map((client) => client.client + LIST_ITEM_SEPARATOR + client.account),
        }
      : filter,
  );
};

export const transformFromFormDataToFilters = (
  filters?: (TraineeTagFilterCondition | CalendarEventFilterCondition | CRMTagFilterConditionField)[],
): (TraineeTagFilterCondition | CalendarEventFilterCondition | CRMTagFilterCondition)[] | undefined => {
  return filters?.map((filter) =>
    filter.type === "crmTags"
      ? {
          ...filter,
          emailClients: filter.emailClients.map((client) => {
            const [clientType, account] = client.split(LIST_ITEM_SEPARATOR);
            return {
              client: clientType as EmailClient,
              account: account || "",
            };
          }),
        }
      : filter,
  );
};

export type FiltersFieldProps = {
  formController?: FormInstance<any> &
    FormInstance<{
      filters?: (TraineeTagFilterCondition | CalendarEventFilterCondition | CRMTagFilterConditionField)[];
      filterOperator?: FilterOperator;
    }>;
  /** Off where the filters are always combined with AND (bulk chat messages). */
  showFilterOperator?: boolean;
};

export const FiltersField = ({ formController, showFilterOperator = true }: FiltersFieldProps) => {
  const { t } = useTranslation(["dashboard", "common", "products"]);
  const { options: traineeTags } = useTagOptions("traineeTags");
  const { tags: crmTags, listOrAccountOptions } = useEmailLists();

  const filters = Form.useWatch("filters", formController);

  return (
    <>
      {showFilterOperator && filters && filters.length > 1 && (
        <Form.Item
          name="filterOperator"
          label={t("automatedMessageForm.filterOperator")}
          tooltip={t("automatedMessageForm.filterOperatorTooltip")}
        >
          <Segmented
            options={[
              { label: "OR", value: "OR" },
              { label: "AND", value: "AND" },
            ]}
          />
        </Form.Item>
      )}
      <Form.Item label={t("automatedMessageForm.filters")}>
        <Form.List name="filters">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => {
                const filterType = filters?.[field.name]?.type;

                return (
                  <div
                    key={`${field.name}-${field.key}-${filterType || "none"}`}
                    style={{
                      display: "flex",
                      width: "100%",
                      alignItems: "flex-start",
                      marginBottom: 16,
                      padding: 16,
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      minHeight: `${typeToHeight[filterType || "none"]}px`,
                    }}
                  >
                    <div className="flex-1 space-y-12">
                      <Form.Item
                        {...field}
                        key={`field-${field.name}-type`}
                        name={[field.name, "type"]}
                        layout="vertical"
                        label={t("automatedMessageForm.filterType")}
                        rules={[
                          {
                            required: true,
                            message: t<string>("common:validationErrors.fieldIsRequired"),
                          },
                        ]}
                      >
                        <Select
                          placeholder={t("automatedMessageForm.selectFilterType")}
                          onChange={() => {
                            // Clear other fields when filter type changes
                            formController?.setFieldValue(["filters", field.name, "condition"], undefined);
                            formController?.setFieldValue(["filters", field.name, "tags"], undefined);
                            formController?.setFieldValue(["filters", field.name, "range"], undefined);
                            formController?.setFieldValue(["filters", field.name, "requiredTypes"], undefined);
                          }}
                        >
                          <Select.Option value="calendarEvents">
                            {t("automatedMessageForm.calendarEvents")}
                          </Select.Option>
                          <Select.Option value="tags">{t("automatedMessageForm.traineeTags")}</Select.Option>
                          <Select.Option value="crmTags">{t("automatedMessageForm.crmTags")}</Select.Option>
                        </Select>
                      </Form.Item>

                      {filterType === "calendarEvents" && (
                        <>
                          <Form.Item
                            {...field}
                            key={`field-${field.name}-condition`}
                            layout="vertical"
                            name={[field.name, "condition"]}
                            label={t("automatedMessageForm.condition")}
                            rules={[
                              {
                                required: true,
                                message: t<string>("common:validationErrors.fieldIsRequired"),
                              },
                            ]}
                          >
                            <Select placeholder={t("automatedMessageForm.selectCondition")}>
                              <Select.Option value="hasCalendarEvent">
                                {t("automatedMessageForm.hasCalendarEvent")}
                              </Select.Option>
                              <Select.Option value="notHasCalendarEvent">
                                {t("automatedMessageForm.notHasCalendarEvent")}
                              </Select.Option>
                            </Select>
                          </Form.Item>

                          <Form.Item
                            {...field}
                            key={`field-${field.name}-range`}
                            layout="vertical"
                            name={[field.name, "range"]}
                            label={t("automatedMessageForm.timeRange")}
                            rules={[
                              {
                                required: true,
                                message: t<string>("common:validationErrors.fieldIsRequired"),
                              },
                            ]}
                          >
                            <Select placeholder={t("automatedMessageForm.selectTimeRange")}>
                              <Select.Option value="today">{t("automatedMessageForm.today")}</Select.Option>
                              <Select.Option value="thisWeek">{t("automatedMessageForm.thisWeek")}</Select.Option>
                              <Select.Option value="thisMonth">{t("automatedMessageForm.thisMonth")}</Select.Option>
                              <Select.Option value="thisYear">{t("automatedMessageForm.thisYear")}</Select.Option>
                              <Select.Option value="nextWeek">{t("automatedMessageForm.nextWeek")}</Select.Option>
                              <Select.Option value="thisAndNextWeek">
                                {t("automatedMessageForm.thisAndNextWeek")}
                              </Select.Option>
                            </Select>
                          </Form.Item>

                          <Form.Item
                            {...field}
                            layout="vertical"
                            name={[field.name, "requiredTypes"]}
                            label={t("automatedMessageForm.eventTypes")}
                            rules={[
                              {
                                required: true,
                                message: t<string>("common:validationErrors.fieldIsRequired"),
                              },
                            ]}
                          >
                            <Select mode="multiple" placeholder={t("automatedMessageForm.selectEventTypes")}>
                              <Select.Option value={CalendarEventType.ONLINE_CONSULTATIONS}>
                                {t("automatedMessageForm.onlineConsultations")}
                              </Select.Option>
                            </Select>
                          </Form.Item>
                        </>
                      )}

                      {filterType === "tags" && (
                        <>
                          <Form.Item
                            {...field}
                            key={`field-${field.name}-condition`}
                            layout="vertical"
                            name={[field.name, "condition"]}
                            label={t("automatedMessageForm.condition")}
                            rules={[
                              {
                                required: true,
                                message: t<string>("common:validationErrors.fieldIsRequired"),
                              },
                            ]}
                          >
                            <Select placeholder={t("automatedMessageForm.selectCondition")}>
                              <Select.Option value="hasTag">{t("automatedMessageForm.hasTag")}</Select.Option>
                              <Select.Option value="notHasTag">{t("automatedMessageForm.notHasTag")}</Select.Option>
                            </Select>
                          </Form.Item>

                          <Form.Item
                            {...field}
                            key={`field-${field.name}-tags`}
                            layout="vertical"
                            name={[field.name, "tags"]}
                            label={t("automatedMessageForm.traineeTags")}
                            rules={[
                              {
                                required: true,
                                message: t<string>("common:validationErrors.fieldIsRequired"),
                              },
                            ]}
                          >
                            <Select
                              mode="multiple"
                              options={traineeTags}
                              placeholder={t("automatedMessageForm.selectTags")}
                            />
                          </Form.Item>
                        </>
                      )}

                      {filterType === "crmTags" && (
                        <>
                          <Form.Item
                            {...field}
                            key={`field-${field.name}-condition`}
                            name={[field.name, "condition"]}
                            layout="vertical"
                            label={t("automatedMessageForm.condition")}
                            rules={[
                              {
                                required: true,
                                message: t<string>("common:validationErrors.fieldIsRequired"),
                              },
                            ]}
                          >
                            <Select placeholder={t("automatedMessageForm.selectCondition")}>
                              <Select.Option value="hasTag">{t("automatedMessageForm.hasTag")}</Select.Option>
                              <Select.Option value="notHasTag">{t("automatedMessageForm.notHasTag")}</Select.Option>
                            </Select>
                          </Form.Item>

                          <Form.Item
                            {...field}
                            key={`field-${field.name}-emailClients`}
                            layout="vertical"
                            name={[field.name, "emailClients"]}
                            label={t<string>("products:form.emailClients")}
                            rules={[
                              {
                                required: true,
                                message: t<string>("common:validationErrors.fieldIsRequired"),
                              },
                            ]}
                          >
                            <TreeSelect
                              style={{ maxWidth: 700 }}
                              treeData={listOrAccountOptions.filter(
                                (account) => account.value !== EmailClient.FreshMail,
                              )}
                              showCheckedStrategy={TreeSelect.SHOW_CHILD}
                              treeCheckable
                              treeNodeLabelProp="title"
                            />
                          </Form.Item>

                          <Form.Item
                            {...field}
                            key={`field-${field.name}-tags`}
                            layout="vertical"
                            name={[field.name, "tags"]}
                            label={t("automatedMessageForm.crmTags")}
                            rules={[
                              {
                                required: true,
                                message: t<string>("common:validationErrors.fieldIsRequired"),
                              },
                            ]}
                          >
                            <Select
                              mode="multiple"
                              options={crmTags}
                              placeholder={t("automatedMessageForm.selectTags")}
                            />
                          </Form.Item>
                        </>
                      )}
                    </div>

                    <MinusCircleOutlined onClick={() => remove(field.name)} style={{ marginLeft: 12, marginTop: 8 }} />
                  </div>
                );
              })}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ marginTop: 10 }}>
                  {t("automatedMessageForm.addFilter")}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>
    </>
  );
};
